import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { apiErrorInterceptor } from '@core/data/api/api-error.interceptor';

import {
  AutoRefreshTokenService,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  UserActivityService,
  createInterceptorCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
  withAutoRefreshToken,
} from 'keycloak-angular';

import { APP_RUNTIME_CONFIG, AppRuntimeConfig } from './app-config.model';

/**
 * Build a case-insensitive RegExp that matches `apiUrl` and any path beneath it,
 * from the runtime-config string. The bearer interceptor only attaches the token
 * to URLs matching this pattern — so the Keycloak token is never leaked to any
 * host other than our own API.
 */
function apiUrlPattern(apiUrl: string): RegExp {
  const escaped = apiUrl
    .replace(/\/+$/, '') // drop trailing slash
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // escape regex metacharacters
  return new RegExp(`^${escaped}(/.*)?$`, 'i');
}

/**
 * All Keycloak/auth providers, built from the runtime config fetched at startup.
 *
 * - `onLoad: 'login-required'` — an unauthenticated visitor goes straight to the
 *   Keycloak sign-in page. No page of ours in between.
 * - PKCE S256 — this is a public SPA client, so no client secret is used.
 * - `withAutoRefreshToken` + activity tracking — silently refreshes the access
 *   token while the user is active and logs them out after inactivity.
 * - the bearer interceptor attaches the token only to `apiUrl` requests.
 *
 * The required-role check (only authorized people may use the app) lives in the
 * route guard, not here — see authn.guard.ts.
 */
/**
 * How long somebody can be idle before the app stops keeping their session alive.
 *
 * **This MUST NOT be shorter than the realm's SSO Session Idle**, or the app
 * becomes the thing that ends a session Keycloak was still happy with — and
 * every re-authentication costs a one-time code, because two-factor is asked on
 * every sign-in, not once per device. Keycloak has no stock "trust this device
 * for a fortnight": the only way to be asked for a code less often is to sign in
 * less often, which means a session that lives longer.
 *
 * Fourteen days, to pair with Remember Me — see `docs/keycloak-invite-and-otp.md`.
 * It is deliberately generous because it cannot, on its own, keep anybody signed
 * in: the realm decides that, and this only governs whether the app keeps
 * refreshing a token while the realm would still renew it. Too LONG is harmless;
 * too short is the bug.
 */
const IDLE_WINDOW_MS = 14 * 24 * 60 * 60 * 1000;

export function provideAuth(config: AppRuntimeConfig): EnvironmentProviders {
  const bearerCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
    urlPattern: apiUrlPattern(config.apiUrl || 'https://__no_api_configured__'),
  });

  return makeEnvironmentProviders([
    { provide: APP_RUNTIME_CONFIG, useValue: config },

    provideKeycloak({
      config: {
        url: config.keycloak.url,
        realm: config.keycloak.realm,
        clientId: config.keycloak.clientId,
      },
      initOptions: {
        /**
         * login-required: arriving at the app unauthenticated sends you straight
         * to the Keycloak sign-in page. Signing in is ONE form, on the one page
         * that takes credentials.
         *
         * **Why this is safe now, when it was not before.** The old bug — "Your
         * login attempt timed out. Login will start from the beginning.", every
         * morning, on the first try — was never caused by going straight here. It
         * was caused by the INACTIVITY LOGOUT below redirecting a tab nobody was
         * looking at: the browser was parked on a sign-in page from the moment the
         * app gave up on the session, and that page's authentication session died
         * half an hour later while the tab sat there overnight.
         *
         * Nothing redirects unattended any more (see `onInactivityTimeout`), so
         * the sign-in page is only ever opened by something somebody just did —
         * arriving at the app, or a 401 on a click — and is never older than it.
         */
        onLoad: 'login-required',
        pkceMethod: 'S256',
        // Silent SSO iframe checks are disabled to avoid third-party-cookie
        // breakage behind the reverse proxy; token freshness is handled by
        // withAutoRefreshToken below instead.
        checkLoginIframe: false,
        silentCheckSsoRedirectUri: `${window.location.origin}/assets/silent-check-sso.html`,
      },
      features: [
        // Silently refresh the access token shortly before it expires while the
        // user is active, and only log them out after a genuine INACTIVITY window.
        //
        // `sessionTimeout` is that inactivity window (no mouse/keyboard activity),
        // NOT the access-token lifespan. It was previously 5 min, which logged a
        // user out for merely reading a page for five minutes — the "kicked out in
        // <10 min" bug. Then 1 hour, to match the realm's SSO Session Idle.
        //
        // An hour was still the thing that made DROS ask for a one-time code
        // several times a day: an hour's reading ended the session, and the next
        // click was a full sign-in, and every sign-in is challenged. See
        // IDLE_WINDOW_MS.
        // `'none'` on timeout, NOT `'logout'`. Logging out here is what parked the
        // browser on a sign-in page that then went stale — it fires on a timer, so
        // it fires at 3am on a tab nobody has touched. Doing nothing leaves the tab
        // where it is; the token quietly dies, and the next thing they actually do
        // gets a 401, which signs them in again on a page as fresh as the click.
        withAutoRefreshToken({ onInactivityTimeout: 'none', sessionTimeout: IDLE_WINDOW_MS }),
      ],
      providers: [AutoRefreshTokenService, UserActivityService],
    }),

    // HttpClient with the bearer interceptor, ready for API calls. The app has no
    // HTTP consumers today (data comes from LocalStorageRepository), so this is
    // inert until a real ApiRepository starts calling `apiUrl`.
    // apiErrorInterceptor runs OUTSIDE the bearer one so it sees the final
    // response, including a 401 raised after the token was attached.
    provideHttpClient(withInterceptors([apiErrorInterceptor, includeBearerTokenInterceptor])),
    { provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG, useValue: [bearerCondition] },
  ]);
}
